import React from 'react';

function pluralize(count, singular, plural = `${singular}s`) {
  return count === 1 ? singular : plural;
}

export default function ComponentsCatalogView({ selectedSystem, componentsList, componentMetricValue }) {
  const componentsWithLinkedFindings = componentsList.filter(
    (component) => component.contribution_summary.active_linked_finding_count > 0
  ).length;
  const componentsWithWorkflowImpact = componentsList.filter(
    (component) => component.contribution_summary.impacted_workflow_count > 0
  ).length;

  return (
    <section className="content-grid single-column">
      <div className="panel detail-panel">
        <div className="panel-header">
          <div>
            <p className="detail-kicker">System map</p>
            <h2>Components</h2>
            <p className="panel-copy">
              Review the ranked components driving current reliability pain, then open a component detail page for workflow and finding context.
            </p>
          </div>
          <div className="form-actions">
            <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/architecture`}>
              View architecture
            </a>
            <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/findings`}>
              Review findings
            </a>
          </div>
        </div>

        <div className="findings-summary-grid">
          <div className="detail-card summary-card">
            <span className="summary-card-label">Ranked components</span>
            <strong>{componentsList.length}</strong>
          </div>
          <div className="detail-card summary-card">
            <span className="summary-card-label">Linked hotspots</span>
            <strong>{componentsWithLinkedFindings}</strong>
          </div>
          <div className="detail-card summary-card">
            <span className="summary-card-label">Workflow impact</span>
            <strong>{componentsWithWorkflowImpact}</strong>
          </div>
        </div>

        {!componentsList.length ? (
          <div className="empty-state-card">
            <strong>No components ranked yet</strong>
            <p>Components will appear here after runtime data has been mapped and at least one completed analysis is available.</p>
          </div>
        ) : (
          <div className="stack component-catalog-stack">
            {componentsList.every((component) => component.health_summary.status === 'no_recent_analysis') ? (
              <div className="empty-state-card">
                <strong>Completed component analysis is not available yet</strong>
                <p>The mapping is in place, but component health and ranking will sharpen once a completed analysis run is available.</p>
              </div>
            ) : null}
            {componentsList.some((component) => component.health_summary.status !== 'no_recent_analysis') &&
            componentsList.every((component) => component.contribution_summary.active_linked_finding_count === 0) ? (
              <div className="empty-state-card">
                <strong>No active linked findings right now</strong>
                <p>Current component health is available, but there are no active localized findings attached to these components.</p>
              </div>
            ) : null}

            {componentsList.map((component) => (
              <a className="detail-card overview-row-link component-catalog-card" href={`#/systems/${selectedSystem.id}/components/${component.id}`} key={component.id}>
                <div className="component-catalog-header">
                  <div className="component-catalog-heading">
                    <div className="component-catalog-title">
                      <p className="card-kicker">{component.mapping_summary.provenance_badge}</p>
                      <strong>{component.display_name || component.name}</strong>
                      <p>
                        {component.component_type} · {component.status}
                        {component.owner ? ` · ${component.owner}` : ''}
                      </p>
                      <p className="overview-note">Rank #{component.pain_rank}</p>
                    </div>
                    <div className="finding-score-pill component-rank-pill">
                      <span className="summary-card-label">Pain rank</span>
                      <strong>#{component.pain_rank}</strong>
                    </div>
                  </div>
                  <div className="chip-row component-catalog-identity">
                    {component.repository ? <span className="chip">Repo: {component.repository}</span> : null}
                    {component.service_name ? <span className="chip">Service: {component.service_name}</span> : null}
                  </div>
                </div>

                <div className="component-catalog-facts">
                  <div className="component-catalog-stat">
                    <span>Linked findings</span>
                    <strong>{component.contribution_summary.active_linked_finding_count} linked findings</strong>
                  </div>
                  <div className="component-catalog-stat">
                    <span>Workflow impact</span>
                    <strong>
                      {component.contribution_summary.impacted_workflow_count}{' '}
                      {pluralize(component.contribution_summary.impacted_workflow_count, 'workflow')}
                    </strong>
                  </div>
                  <div className="component-catalog-stat">
                    <span>Runtime mapping</span>
                    <strong>
                      {component.mapping_summary.observed_run_count} runs · {component.mapping_summary.observed_step_count} steps
                    </strong>
                  </div>
                </div>

                {component.health_summary.metrics.length ? (
                  <div className="chip-row component-catalog-metrics">
                    {component.health_summary.metrics.map((metric) => (
                      <span className="chip" key={`${component.id}-${metric.metric_name}`}>
                        {metric.display_label}: {componentMetricValue(metric)}
                      </span>
                    ))}
                  </div>
                ) : null}
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
